import React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import axios from 'axios';
import {
  format,
  parseISO,
  subDays,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { POSITION_HISTORY } from '../../infrastructure/config/api';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slider,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  FilterAlt as FilterIcon,
  FilterAltOff as FilterAltOffIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  FileDownload as ExportIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../infrastructure/config/api';

// Date presets
const DATE_RANGES = [
  { label: '7 ngày', value: 7 },
  { label: '30 ngày', value: 30 },
  { label: '3 tháng', value: 90 },
  { label: 'Tùy chỉnh', value: 'custom' }
];

// Trade type options
const TRADE_TYPES = [
  { value: 'all', label: 'Tất cả' },
  { value: 'LONG', label: 'Long' },
  { value: 'SHORT', label: 'Short' }
];

// Trade result options
const TRADE_RESULTS = [
  { value: 'all', label: 'Tất cả' },
  { value: 'profit', label: 'Lãi' },
  { value: 'loss', label: 'Lỗ' }
];

// Format number with commas and optional decimal places
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  const number = typeof num === 'string' ? parseFloat(num) : num;
  return number.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  });
};

// Export data to CSV file
const exportToCSV = (data, filename) => {
  if (!data || !data.length) return;

  try {
    // Extract headers from the first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    let csvContent = headers.join(',') + '\n';

    // Add rows
    data.forEach(item => {
      const row = headers.map(header => {
        // Handle nested objects and arrays
        const value = item[header];
        if (value === null || value === undefined) return '';
        // Convert to string and escape quotes
        const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
        return `"${str.replace(/"/g, '""')}"`;
      });
      csvContent += row.join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
  }
};

const PositionHistoryDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();

  // State for positions and loading
  const [allPositions, setAllPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Track the previous symbol to detect changes
  const prevSymbolRef = useRef();

  // Auto-sync position history when component mounts or symbol changes
  useEffect(() => {
    if (symbol) {
      const isSymbolChanged = prevSymbolRef.current !== symbol;
      prevSymbolRef.current = symbol;

      if (isSymbolChanged) {
        console.log(`[PositionHistoryDetail] Symbol changed to ${symbol}, syncing position history...`);
        syncPositionHistory();
      } else {
        // Only fetch on initial mount or when explicitly requested
        console.log(`[PositionHistoryDetail] Loading position history for ${symbol}...`);
        fetchPositionHistory();
      }
    }
  }, [symbol]);

  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    total: 0
  });

  // Filter states
  const [dateRange, setDateRange] = useState(30); // Default to 30 days
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());

  const [tradeType, setTradeType] = useState('all');
  const [tradeResult, setTradeResult] = useState('all');
  const [leverageRange, setLeverageRange] = useState([0, 100]);
  const [showFilters, setShowFilters] = useState(false);

  // Summary state
  const [summary, setSummary] = useState({
    totalPnl: 0,
    totalTrades: 0,
    totalVolume: 0,
    winRate: 0,
    winningTrades: 0,
    losingTrades: 0,
    avgLeverage: 0
  });

  // Sync position history from Binance
  const syncPositionHistory = async () => {
    if (!symbol) return;

    // Don't sync if already syncing
    if (syncing) {
      console.log('[PositionHistoryDetail] Sync already in progress, skipping...');
      return;
    }

    try {
      console.log(`[PositionHistoryDetail] Syncing position history for ${symbol}`);

      // Show a subtle loading state only in the sync button
      setSyncing(true);

      // Clear any previous errors
      setError(null);

      // Call the sync endpoint with the current symbol
      const response = await api.post(POSITION_HISTORY.SYNC(symbol), {});

      if (response.data?.success) {
        const message = response.data.syncedCount > 0
          ? `Đã đồng bộ ${response.data.syncedCount} vị thế mới từ Binance`
          : 'Không có vị thế mới nào để đồng bộ';

        console.log(`[PositionHistoryDetail] Sync successful for ${symbol}`, response.data);

        // Show success message
        setError({
          type: 'success',
          message: message,
          data: response.data
        });

        // Refresh the position history in the background
        fetchPositionHistory();
      } else {
        throw new Error(response.data?.message || 'Không thể đồng bộ lịch sử giao dịch');
      }
    } catch (error) {
      console.error('Error syncing position history:', error);

      // Only show error if it's not a cancellation
      if (!axios.isCancel(error)) {
        const errorMessage = error.response?.data?.message ||
          error.message ||
          'Đã xảy ra lỗi khi đồng bộ dữ liệu từ Binance';

        setError({
          type: 'error',
          message: errorMessage,
          details: error.response?.data?.details || error.response?.data
        });
      }
    } finally {
      // Reset syncing state after a small delay to prevent UI flicker
      setTimeout(() => setSyncing(false), 500);
    }
  };

  // Fetch position history with pagination
  const fetchPositionHistory = async () => {
    if (!symbol) return;

    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = {
        startDate: startOfDay(startDate).toISOString(),
        endDate: endOfDay(endDate).toISOString(),
        page: pagination.page + 1, // 1-based for API, 0-based for UI
        limit: pagination.rowsPerPage
      };

      console.log(`[PositionHistoryDetail] Fetching position history for ${symbol} with params:`, params);

      const response = await api.get(POSITION_HISTORY.GET_BY_SYMBOL(symbol), { params });

      if (!response.data) {
        throw new Error('No data in response');
      }

      if (response.data.success === false) {
        throw new Error(response.data.message || 'API request failed');
      }

      const { data: positions, summary, pagination: paginationData } = response.data;

      if (!Array.isArray(positions)) {
        throw new Error('Expected positions data to be an array');
      }

      console.log(`[PositionHistoryDetail] Fetched ${positions.length} positions for ${symbol}`, {
        positions,
        summary,
        pagination: paginationData
      });

      // Update positions and summary
      setAllPositions(positions);
      setSummary(prev => ({
        ...prev,
        totalPnl: parseFloat(summary?.totalPnl || 0),
        totalTrades: summary?.totalTrades || 0,
        totalVolume: parseFloat(summary?.totalVolume || 0),
        winRate: parseFloat(summary?.winRate || 0),
        winningTrades: summary?.winningTrades || 0,
        losingTrades: summary?.losingTrades || 0,
        avgLeverage: parseFloat(summary?.avgLeverage || 0)
      }));

      // Update pagination state
      if (paginationData) {
        setPagination(prev => ({
          ...prev,
          total: paginationData.total || 0,
          page: (paginationData.currentPage || 1) - 1, // Convert to 0-based for UI
          rowsPerPage: paginationData.perPage || prev.rowsPerPage
        }));
      }

    } catch (error) {
      console.error('Error fetching position history:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load position history');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setPagination({
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
      total: pagination.total
    });
  };

  // Initial fetch on component mount and when symbol/date range or pagination changes
  useEffect(() => {
    fetchPositionHistory();
  }, [symbol, startDate, endDate, pagination.page, pagination.rowsPerPage]);

  // Apply filters to positions
  const filteredPositions = useMemo(() => {
    return allPositions.filter(position => {
      // Filter by trade type
      if (tradeType !== 'all' && position.type !== tradeType) return false;

      // Filter by trade result
      if (tradeResult !== 'all') {
        const isProfit = parseFloat(position.pnl) > 0;
        if ((tradeResult === 'profit' && !isProfit) || (tradeResult === 'loss' && isProfit)) {
          return false;
        }
      }

      // Filter by leverage
      const leverage = parseFloat(position.leverage) || 0;
      if (leverage < leverageRange[0] || leverage > leverageRange[1]) return false;

      return true;
    });
  }, [allPositions, tradeType, tradeResult, leverageRange]);

  // Calculate summary
  useEffect(() => {
    if (allPositions.length === 0) return;

    const totalPnl = filteredPositions.reduce((sum, pos) => sum + (parseFloat(pos.pnl) || 0), 0);
    const totalTrades = filteredPositions.length;

    const volumeData = filteredPositions.reduce((acc, pos) => {
      const quantity = parseFloat(pos.quantity) || 0;
      const entryPrice = parseFloat(pos.entry_price) || 0;
      return {
        totalVolume: acc.totalVolume + (quantity * entryPrice),
        totalLeverage: acc.totalLeverage + (parseFloat(pos.leverage) || 0)
      };
    }, { totalVolume: 0, totalLeverage: 0 });

    const winningTrades = filteredPositions.filter(pos => (parseFloat(pos.pnl) || 0) > 0).length;
    const losingTrades = filteredPositions.filter(pos => (parseFloat(pos.pnl) || 0) < 0).length;

    setSummary({
      totalPnl,
      totalTrades,
      totalVolume: volumeData.totalVolume,
      winRate: totalTrades > 0 ? Math.round((winningTrades / totalTrades) * 100) : 0,
      winningTrades,
      losingTrades,
      avgLeverage: totalTrades > 0 ? Math.round((volumeData.totalLeverage / totalTrades) * 10) / 10 : 0
    });
  }, [filteredPositions]);

  // Handle date range change
  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'custom') {
      setStartDate(subDays(new Date(), value));
      setEndDate(new Date());
    }
  };

  // Using the formatNumber and exportToCSV functions defined at the top of the file

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              Lịch sử giao dịch: {symbol}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Đồng bộ dữ liệu từ Binance">
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={syncPositionHistory}
                  disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                >
                  {syncing ? 'Đang đồng bộ...' : 'Đồng bộ'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Đang tải dữ liệu...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error?.type === 'error') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h6" gutterBottom>
          Đã xảy ra lỗi
        </Typography>
        <Typography color="text.secondary" paragraph>
          {error.message}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
        >
          Thử lại
        </Button>
      </Box>
    );
  }

  if (error?.type === 'success') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="success.main" variant="h6" gutterBottom>
          Thành công!
        </Typography>
        <Typography color="text.secondary" paragraph>
          {error.message}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setError(null)}
          sx={{ mt: 2 }}
        >
          Quay lại
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              Lịch sử giao dịch: {symbol}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Đồng bộ dữ liệu từ Binance">
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={syncPositionHistory}
                  disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                >
                  {syncing ? 'Đang đồng bộ...' : 'Đồng bộ'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Date Range Picker */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Khoảng thời gian</InputLabel>
            <Select
              value={dateRange}
              label="Khoảng thời gian"
              onChange={(e) => handleDateRangeChange(e.target.value)}
            >
              {DATE_RANGES.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {dateRange === 'custom' && (
            <>
              <DatePicker
                label="Từ ngày"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                renderInput={(params) => <TextField {...params} size="small" sx={{ width: 180 }} />}
              />
              <DatePicker
                label="Đến ngày"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                renderInput={(params) => <TextField {...params} size="small" sx={{ width: 180 }} />}
              />
            </>
          )}

          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            size="small"
            sx={{ ml: 'auto', order: { xs: 1, sm: 0 } }}
          >
            {showFilters ? 'Ẩn bộ lọc' : 'Bộ lọc nâng cao'}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchPositionHistory}
            disabled={loading}
            size="small"
            sx={{ ml: 1, order: { xs: 2, sm: 0 } }}
          >
            {loading ? 'Đang tải...' : 'Làm mới'}
          </Button>
          <Tooltip title="Đồng bộ dữ liệu từ Binance">
            <span>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={syncPositionHistory}
                disabled={syncing || loading}
                size="small"
                sx={{ ml: 1, order: { xs: 2, sm: 0 } }}
                color="secondary"
              >
                {syncing ? 'Đang đồng bộ...' : 'Đồng bộ từ Binance'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Advanced Filters */}
        {showFilters && (
          <Paper elevation={2} sx={{ p: 2, mt: 1, mb: 3, backgroundColor: 'background.paper' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Bộ lọc nâng cao
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Loại lệnh</InputLabel>
                  <Select
                    value={tradeType}
                    label="Loại lệnh"
                    onChange={(e) => setTradeType(e.target.value)}
                  >
                    {TRADE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kết quả</InputLabel>
                  <Select
                    value={tradeResult}
                    label="Kết quả"
                    onChange={(e) => setTradeResult(e.target.value)}
                  >
                    {TRADE_RESULTS.map((result) => (
                      <MenuItem key={result.value} value={result.value}>
                        {result.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Box sx={{ px: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Đòn bẩy: {leverageRange[0]}x - {leverageRange[1]}x
                  </Typography>
                  <Slider
                    value={leverageRange}
                    onChange={(_, newValue) => setLeverageRange(newValue)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                    step={1}
                    valueLabelFormat={(value) => `${value}x`}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper sx={{ p: 2, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 8, right: 8, opacity: 0.1 }}>
                <TrendingUpIcon fontSize="large" color={summary.totalPnl >= 0 ? 'success' : 'error'} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">Tổng Lãi/Lỗ</Typography>
              <Typography
                variant="h6"
                sx={{
                  color: summary.totalPnl >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 'bold'
                }}
              >
                {summary.totalPnl >= 0 ? '+' : ''}{formatNumber(summary.totalPnl, 2)} USDT
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {summary.winningTrades} thắng / {summary.losingTrades} thua
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block">Số giao dịch</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{summary.totalTrades} lệnh</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {summary.winRate}% tỷ lệ thắng
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block">Khối lượng</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumber(summary.totalVolume)} {symbol?.replace('USDT', '')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                ~{formatNumber(summary.totalVolume * 1000, 0)} USDT
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4} md={2.4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" display="block">Đòn bẩy TB</Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{summary.avgLeverage}x</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {leverageRange[0]}x - {leverageRange[1]}x
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4} md={2.4}>
            <Paper sx={{ p: 2, height: '100%', backgroundColor: 'primary.main' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="primary.contrastText" display="block">Tỷ lệ thắng</Typography>
                  <Typography variant="h6" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                    {summary.winRate}%
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <Typography variant="subtitle2" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                    {summary.winRate}%
                  </Typography>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: '50%',
                      border: '4px solid',
                      borderColor: 'success.main',
                      borderRightColor: 'transparent',
                      transform: `rotate(${summary.winRate * 3.6}deg)`,
                      transition: 'transform 0.5s ease-in-out'
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Positions Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }} elevation={3}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" color="text.secondary">
              {pagination.total} giao dịch được tìm thấy
            </Typography>
            <Box>
              <Button
                size="small"
                startIcon={<ExportIcon />}
                onClick={() => exportToCSV(filteredPositions, `${symbol}_trading_history`)}
                disabled={filteredPositions.length === 0}
              >
                Xuất Excel
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)', minHeight: 200 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Thời gian</TableCell>
                  <TableCell>Loại lệnh</TableCell>
                  <TableCell align="right">Giá vào</TableCell>
                  <TableCell align="right">Giá đóng</TableCell>
                  <TableCell align="right">Khối lượng</TableCell>
                  <TableCell align="right">Đòn bẩy</TableCell>
                  <TableCell align="right">Lãi/Lỗ</TableCell>
                  <TableCell>Trạng thái</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPositions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <FilterAltOffIcon fontSize="large" color="disabled" />
                        <Typography color="text.secondary">
                          Không tìm thấy giao dịch nào phù hợp
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setTradeType('all');
                            setTradeResult('all');
                            setLeverageRange([0, 100]);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Xóa bộ lọc
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPositions.map((position) => (
                    <TableRow
                      key={position.id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        <Tooltip title={format(parseISO(position.close_time), 'PPpp')}>
                          <Typography variant="body2">
                            {format(parseISO(position.close_time), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={position.side === 'BUY' ? 'LONG' : 'SHORT'}
                          color={position.side === 'BUY' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{formatNumber(position.entry_price, 4)}</TableCell>
                      <TableCell align="right">{formatNumber(position.exit_price, 4)}</TableCell>
                      <TableCell align="right">{formatNumber(position.quantity, 4)}</TableCell>
                      <TableCell align="right">{position.leverage}x</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: position.pnl >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 500
                        }}
                      >
                        {position.pnl >= 0 ? '+' : ''}{formatNumber(position.pnl, 2)} USDT
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={position.status === 'closed' ? 'Đã đóng' : position.status}
                          color={position.status === 'closed' ? 'default' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.total > 0 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Hiển thị {Math.min(pagination.page * pagination.rowsPerPage + 1, pagination.total)} -
                {Math.min((pagination.page + 1) * pagination.rowsPerPage, pagination.total)}
                trong tổng số {pagination.total} giao dịch
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={pagination.rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Số dòng mỗi trang' }}
                    disabled={pagination.total === 0}
                  >
                    {[10, 25, 50, 100].map((rows) => (
                      <MenuItem key={rows} value={rows}>
                        {rows} dòng/trang
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Trang trước">
                    <span>
                      <IconButton
                        onClick={() => handleChangePage(null, pagination.page - 1)}
                        disabled={pagination.page === 0 || loading}
                        aria-label="Trang trước"
                      >
                        <ArrowBackIosIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Typography variant="body2" sx={{ mx: 1, minWidth: 100, textAlign: 'center' }}>
                    Trang {pagination.page + 1} / {Math.max(1, Math.ceil(pagination.total / pagination.rowsPerPage))}
                  </Typography>

                  <Tooltip title="Trang sau">
                    <span>
                      <IconButton
                        onClick={() => handleChangePage(null, pagination.page + 1)}
                        disabled={pagination.page >= Math.ceil(pagination.total / pagination.rowsPerPage) - 1 || loading}
                        aria-label="Trang sau"
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          )}

          {filteredPositions.length > 0 && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" align="right">
                Đang hiển thị {filteredPositions.length} giao dịch gần nhất
              </Typography>
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Quay lại
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default PositionHistoryDetail;
